import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { Empty, Field, Loading, PageHead, Panel, toast } from '../components/ui';
import { errorMessage } from '../format';
import { useApi } from '../hooks';
import { FACET_FIELDS, RELATION, UNDERSTANDING } from '../labels';
import { go } from '../router';
import type { ItemInput, Relation } from '../types';

const EMPTY: ItemInput = {
  title: '', definition: '', principle: '', examples: '', applications: '', counterexamples: '', notes: '',
  domain: '', tags: [], source_title: '', source_url: '', source_ref: '', understanding_self: 3, links: [],
};

export function KnowledgeEdit({ id }: { id?: number }) {
  const [form, setForm] = useState<ItemInput>(EMPTY);
  const [tagText, setTagText] = useState('');
  const [loaded, setLoaded] = useState(!id);
  const [saving, setSaving] = useState(false);
  const [linkTo, setLinkTo] = useState<number | ''>('');
  const [linkRel, setLinkRel] = useState<Relation>('related');
  const [linkNote, setLinkNote] = useState('');
  const titles = useApi(api.titles);
  const domains = useApi(api.domains);

  useEffect(() => {
    if (!id) return;
    api.item(id).then(
      (d) => {
        const it = d.item;
        setForm({
          title: it.title, definition: it.definition, principle: it.principle, examples: it.examples,
          applications: it.applications, counterexamples: it.counterexamples, notes: it.notes, domain: it.domain,
          tags: it.tags, source_title: it.source_title, source_url: it.source_url, source_ref: it.source_ref,
          understanding_self: it.understanding_self,
          links: d.links.filter((l) => l.direction === 'out').map((l) => ({ to_id: l.other_id, relation: l.relation, note: l.note })),
        });
        setTagText(it.tags.join(', '));
        setLoaded(true);
      },
      (e) => toast(errorMessage(e), 'err'),
    );
  }, [id]);

  const set = <K extends keyof ItemInput>(key: K, value: ItemInput[K]) => setForm((f) => ({ ...f, [key]: value }));
  const filled = FACET_FIELDS.filter((f) => form[f.key].trim());
  const linkCards = form.links.filter((l) => l.note.trim()).length;
  const others = (titles.data ?? []).filter((t) => t.id !== id);
  const titleOf = (tid: number) => titles.data?.find((t) => t.id === tid)?.title ?? `#${tid}`;

  const addLink = () => {
    if (linkTo === '') return;
    set('links', [...form.links.filter((l) => l.to_id !== linkTo), { to_id: linkTo, relation: linkRel, note: linkNote.trim() }]);
    setLinkTo('');
    setLinkRel('related');
    setLinkNote('');
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, tags: tagText.split(',').map((t) => t.trim()).filter(Boolean) };
    try {
      const d = id ? await api.updateItem(id, body) : await api.createItem(body);
      toast(id ? '저장했습니다' : `저장했습니다 — 복습 카드 ${d.cards.length}장`);
      go(`/knowledge/${d.item.id}`);
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <Loading />;
  return (
    <form onSubmit={save} className="edit-form">
      <PageHead title={id ? '개념 고치기' : '새 개념'} sub="책 문장을 옮기지 말고 내 말로. 비워 둔 칸은 카드가 만들어지지 않습니다." />
      <Panel>
        <div className="grid-2">
          <Field label="개념" htmlFor="f-title">
            <input id="f-title" className="input" value={form.title} onChange={(e) => set('title', e.target.value)} required maxLength={200} autoFocus={!id} />
          </Field>
          <Field label="분야" htmlFor="f-domain" hint="분야별 기억 유지율을 따로 봅니다">
            <input id="f-domain" className="input" list="domain-list" value={form.domain} onChange={(e) => set('domain', e.target.value)} maxLength={100} />
            <datalist id="domain-list">
              {domains.data?.map((d) => <option key={d.domain} value={d.domain} />)}
            </datalist>
          </Field>
        </div>
        {FACET_FIELDS.map((f) => (
          <Field key={f.key} label={f.label} htmlFor={`f-${f.key}`} hint={f.hint}>
            <textarea id={`f-${f.key}`} className="input" rows={f.key === 'definition' ? 2 : 3} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} />
          </Field>
        ))}
        <p className="small muted">
          만들어질 복습 카드: <b>{filled.length + linkCards}장</b>
          {filled.length > 0 && ` (${filled.map((f) => f.label).join(' · ')}${linkCards ? ` + 연결 ${linkCards}` : ''})`}
        </p>
      </Panel>

      <Panel title="지금 이해도 (스스로 평가)">
        <div className="scale" role="radiogroup" aria-label="이해도">
          {[1, 2, 3, 4, 5].map((v) => (
            <label key={v} className={`scale-item ${form.understanding_self === v ? 'on' : ''}`}>
              <input type="radio" name="understanding" value={v} checked={form.understanding_self === v} onChange={() => set('understanding_self', v)} />
              <b>{v}</b>
              <span>{UNDERSTANDING[v]}</span>
            </label>
          ))}
        </div>
        <p className="small muted">스스로 매긴 이해도와 실제로 떠올린 결과의 차이도 나중에 비교합니다.</p>
      </Panel>

      <Panel title="관련 개념">
        {form.links.length > 0 && (
          <ul className="list">
            {form.links.map((l) => (
              <li key={l.to_id} className="list-row">
                <span className="grow">
                  <span><b>{titleOf(l.to_id)}</b> <span className="badge">{RELATION[l.relation]}</span></span>
                  {l.note && <span className="muted small">{l.note}</span>}
                </span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => set('links', form.links.filter((x) => x.to_id !== l.to_id))}>빼기</button>
              </li>
            ))}
          </ul>
        )}
        {others.length === 0 ? (
          <Empty>다른 개념이 생기면 서로 연결할 수 있습니다.</Empty>
        ) : (
          <div className="link-add">
            <select className="input" aria-label="연결할 개념" value={linkTo} onChange={(e) => setLinkTo(e.target.value ? Number(e.target.value) : '')}>
              <option value="">연결할 개념 선택</option>
              {others.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <select className="input" aria-label="관계" value={linkRel} onChange={(e) => setLinkRel(e.target.value as Relation)}>
              {(Object.keys(RELATION) as Relation[]).map((r) => <option key={r} value={r}>{RELATION[r]}</option>)}
            </select>
            <input className="input wide" aria-label="관계 설명" placeholder="관계 설명 — 적으면 '연결 카드'가 생깁니다" value={linkNote} onChange={(e) => setLinkNote(e.target.value)} />
            <button type="button" className="btn" onClick={addLink} disabled={linkTo === ''}>연결</button>
          </div>
        )}
      </Panel>

      <Panel title="출처 · 메모">
        <div className="grid-2">
          <Field label="출처 (책·글 제목)" htmlFor="f-src">
            <input id="f-src" className="input" value={form.source_title} onChange={(e) => set('source_title', e.target.value)} />
          </Field>
          <Field label="링크" htmlFor="f-url">
            <input id="f-url" className="input" value={form.source_url} onChange={(e) => set('source_url', e.target.value)} />
          </Field>
          <Field label="Zotero citekey" htmlFor="f-ref">
            <input id="f-ref" className="input" value={form.source_ref} onChange={(e) => set('source_ref', e.target.value)} />
          </Field>
          <Field label="태그 (쉼표로 구분)" htmlFor="f-tags">
            <input id="f-tags" className="input" value={tagText} onChange={(e) => setTagText(e.target.value)} />
          </Field>
        </div>
        <Field label="메모" htmlFor="f-notes">
          <textarea id="f-notes" className="input" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </Panel>

      <div className="form-actions">
        <button type="button" className="btn" onClick={() => history.back()}>취소</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '저장 중…' : '저장'}</button>
      </div>
    </form>
  );
}
